import { useImageRotator } from '../../../services/hooks';
import type { FileInputProps } from '../FileInput.intefaces';

import { useFileInputDocument } from './useFileInputDocument';
import { useFileInputUpload } from './useFileInputUpload';

type UseFileInputOptions = Pick<
  FileInputProps,
  'value' | 'onChange' | 'cache' | 'withAuth' | 'validators'
> &
  Required<
    Pick<
      FileInputProps,
      'loading' | 'disabled' | 'onBlur' | 'onCacheSuccess' | 'onCacheError'
    >
  >;

export const useFileInput = ({
  value,
  onChange,
  onBlur,
  loading,
  disabled,
  cache,
  onCacheSuccess,
  onCacheError,
  withAuth,
  validators,
}: UseFileInputOptions) => {
  const imageRotator = useImageRotator();
  const { blobDocument, cacheFileName, handleChange, load, onDocumentChange } =
    useFileInputDocument({ value, onChange, withAuth });
  const { isFileLoading, validationErrorMessage, invalidFileName, onDrop } =
    useFileInputUpload({
      onChange: handleChange,
      onBlur,
      loading,
      cache,
      onCacheSuccess,
      onCacheError,
      validators,
      onDocumentChange,
    });
  const isLoading = loading || isFileLoading;
  const isDisabled = disabled || isLoading;

  /**
   * Функция rotateDocument вращает загруженный документ.
   * Сначала загружает Blob документа, затем вызывает imageRotator для вращения.
   * Если вращение успешно, создаёт новый файл с обновлённым Blob и вызывает onDrop с этим файлом.
   *
   */
  const rotateDocument = () => {
    load().then((blob: Blob) => {
      imageRotator.rotate(blob).then((updatedBlob: Blob | null) => {
        if (updatedBlob) {
          const format = updatedBlob.type.split('/').pop();

          const file = new File([updatedBlob], `image.${format}`, {
            type: updatedBlob.type,
          });

          onDrop([file]);
        }
      });
    });
  };

  return {
    isLoading,
    isDisabled,
    validationErrorMessage,
    invalidFileName,
    cacheFileName,
    blobDocument,
    handleChange,
    load,
    onDrop,
    rotateDocument,
  };
};
